import { defineStore } from "pinia";
import { questionApi } from "@/services/questionApi";

function normDifficulty(d) {
  return (d ?? "").toString().trim().toLowerCase();
}

function pickRandom(arr) {
  if (!arr.length) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

// Maximum questions to serve per section group per session
const MAX_QUESTIONS_PER_SG = 10;

export const useQuestionStore = defineStore("questionStore", {
  state: () => ({
    // Cache organized by sectionGroupId for efficient lookup
    // { 'WS_ADD': [q1, q2, ...], 'WS_SUB': [q1, q2, ...] }
    _cache: {},

    // Track used question IDs within a session to avoid duplicates
    _usedIds: new Set(),

    // Track how many questions have been served per section group this session
    _servedCountBySG: {},

    // Loading state
    _loading: false,

    // Track which section groups have been prefetched
    _prefetchedGroups: new Set()
  }),

  getters: {
    isLoading: (state) => state._loading,

    /**
     * Get cache stats for debugging
     */
    cacheStats: (state) => {
      const stats = {};
      for (const [sgId, questions] of Object.entries(state._cache)) {
        const available = questions.filter(q => !state._usedIds.has(q._id));
        stats[sgId] = {
          total: questions.length,
          available: available.length,
          used: questions.length - available.length,
          byDifficulty: {
            easy: available.filter(q => q.difficulty === 'easy').length,
            medium: available.filter(q => q.difficulty === 'medium').length,
            hard: available.filter(q => q.difficulty === 'hard').length
          }
        };
      }
      return stats;
    }
  },

  actions: {
    /**
     * Prefetch questions for section groups with stratified difficulty.
     * Call this once before starting a session.
     *
     * @param {string[]} sectionGroupIds - Section groups to prefetch
     * @param {Object} [options]
     * @param {number} [options.perGroup=15] - Questions per section group
     * @returns {Promise<void>}
     */
    async prefetch(sectionGroupIds, { perGroup = 15 } = {}) {
      if (!sectionGroupIds?.length) return;

      this._loading = true;
      try {
        // Calculate total size (capped at 50 by API)
        const totalSize = Math.min(sectionGroupIds.length * perGroup, 50);

        // Fetch with stratified sampling
        const { questions } = await questionApi.getQuestions({
          formatSectionGroups: sectionGroupIds,
          excludeIds: Array.from(this._usedIds),
          size: totalSize,
          stratified: true
        });

        // Organize questions into cache by section group
        for (const question of questions) {
          const sgIds = question.formatsectionGroups || [];
          for (const sgId of sgIds) {
            // Only cache for requested section groups
            if (sectionGroupIds.includes(sgId)) {
              if (!this._cache[sgId]) {
                this._cache[sgId] = [];
              }
              // Avoid duplicates in cache
              if (!this._cache[sgId].some(q => q._id === question._id)) {
                this._cache[sgId].push(question);
              }
            }
          }
        }

        // Mark these groups as prefetched
        for (const sgId of sectionGroupIds) {
          this._prefetchedGroups.add(sgId);
        }

      } catch (err) {
        console.error('Failed to prefetch questions:', err);
      } finally {
        this._loading = false;
      }
    },

    /**
     * Get a question from cache, filtering by difficulty locally.
     * Returns null if no matching question available.
     *
     * @param {string} sectionGroupId - Section group to get from
     * @param {string} [difficulty] - Optional difficulty filter
     * @returns {Object|null} Question or null
     */
    getFromCache(sectionGroupId, difficulty) {
      // Check if we've hit the limit for this section group
      const servedCount = this._servedCountBySG[sectionGroupId] || 0;
      if (servedCount >= MAX_QUESTIONS_PER_SG) {
        return null;
      }

      const pool = this._cache[sectionGroupId] || [];

      // Filter by: not used AND matches difficulty (if specified)
      const available = pool.filter(q => {
        if (this._usedIds.has(q._id)) return false;
        if (difficulty && normDifficulty(q.difficulty) !== normDifficulty(difficulty)) return false;
        return true;
      });

      if (!available.length) return null;

      // Pick random and mark as used
      const question = pickRandom(available);
      this._usedIds.add(question._id);
      this._servedCountBySG[sectionGroupId] = servedCount + 1;
      return question;
    },

    /**
     * Check if a section group has been prefetched
     */
    isPrefetched(sectionGroupId) {
      return this._prefetchedGroups.has(sectionGroupId);
    },

    /**
     * Get available question count for a section group and difficulty
     */
    getAvailableCount(sectionGroupId, difficulty) {
      const pool = this._cache[sectionGroupId] || [];
      return pool.filter(q => {
        if (this._usedIds.has(q._id)) return false;
        if (difficulty && normDifficulty(q.difficulty) !== normDifficulty(difficulty)) return false;
        return true;
      }).length;
    },

    /**
     * Reset session state (clear used IDs).
     * Call this when starting a new session.
     */
    resetSession() {
      this._usedIds.clear();
      this._servedCountBySG = {};
    },

    /**
     * Clear all cache and reset state.
     * Call this when switching students or tracks.
     */
    clearCache() {
      this._cache = {};
      this._usedIds.clear();
      this._servedCountBySG = {};
      this._prefetchedGroups.clear();
    },

    /**
     * Fetch a single question directly from API (fallback when cache is empty).
     * Use sparingly - prefer prefetch + getFromCache.
     *
     * @param {string} sectionGroupId - Section group
     * @param {string} [difficulty] - Difficulty filter
     * @returns {Promise<Object|null>}
     */
    async fetchSingle(sectionGroupId, difficulty) {
      // Check if we've hit the limit for this section group
      const servedCount = this._servedCountBySG[sectionGroupId] || 0;
      if (servedCount >= MAX_QUESTIONS_PER_SG) {
        return null;
      }

      try {
        const { questions } = await questionApi.getQuestions({
          formatSectionGroups: [sectionGroupId],
          difficulty,
          excludeIds: Array.from(this._usedIds),
          size: 1
        });

        if (questions.length) {
          const question = questions[0];
          this._usedIds.add(question._id);
          this._servedCountBySG[sectionGroupId] = servedCount + 1;

          // Also add to cache for potential reuse
          if (!this._cache[sectionGroupId]) {
            this._cache[sectionGroupId] = [];
          }
          this._cache[sectionGroupId].push(question);

          return question;
        }

        return null;
      } catch (err) {
        console.error('Failed to fetch single question:', err);
        return null;
      }
    }
  }
});
